import { EffectivePermissions } from "../Data/role.dto";

export const STUDIO_ACCESS_STORAGE_KEY = "studio_access";

export const getStoredStudioAccess = (): EffectivePermissions | null => {
  try {
    const raw = localStorage.getItem(STUDIO_ACCESS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return {
        is_studio_owner: Boolean(parsed.is_studio_owner),
        studio_role: parsed.studio_role || null,
        permissions: Array.isArray(parsed.permissions) ? parsed.permissions : [],
        modules: Array.isArray(parsed.modules) ? parsed.modules : [],
        all_modules: Array.isArray(parsed.all_modules) ? parsed.all_modules : [],
      };
    }
  } catch (err) {
    console.error("Failed to parse stored studio_access:", err);
  }
  return null;
};

export const setStoredStudioAccess = (access: EffectivePermissions | null | undefined): void => {
  try {
    if (access) {
      localStorage.setItem(STUDIO_ACCESS_STORAGE_KEY, JSON.stringify(access));
    } else {
      localStorage.removeItem(STUDIO_ACCESS_STORAGE_KEY);
    }
  } catch (err) {
    console.error("Failed to persist studio_access to localStorage:", err);
  }
};

export const clearStoredStudioAccess = (): void => {
  try {
    localStorage.removeItem(STUDIO_ACCESS_STORAGE_KEY);
  } catch (err) {
    console.error("Failed to clear studio_access from localStorage:", err);
  }
};

/**
 * Determines the most appropriate default landing route for a user based on their role and studio permissions.
 * Studio Owners (role=3) land on /dashboard.
 * Sub-users (role=4) land on their first permitted module in prioritized order.
 */
export const getDefaultRouteForUser = (
  studioAccess?: EffectivePermissions | null,
  userRole?: number
): string => {
  // Studio owner (role 3) always goes to main dashboard
  if (userRole === 3 || studioAccess?.is_studio_owner === true) {
    return "/dashboard";
  }

  // If we don't have studioAccess information yet, fallback to safe dashboard path
  if (!studioAccess || !Array.isArray(studioAccess.modules)) {
    return "/dashboard/albums/all";
  }

  // Sub-user (role 4) with explicitly resolved studio access having no assigned role goes to login
  if (userRole === 4 && !studioAccess.studio_role && (!studioAccess.modules || studioAccess.modules.length === 0)) {
    return "/auth/login";
  }

  const modules = studioAccess.modules.map((m) => m.toUpperCase());

  if (modules.includes("DASHBOARD")) {
    return "/dashboard";
  }
  if (modules.includes("ALBUMS")) {
    return "/dashboard/albums/all";
  }
  if (modules.includes("EVENTS")) {
    return "/dashboard/events/userCreated";
  }
  if (modules.includes("ORDERS")) {
    return "/dashboard/orders/all";
  }
  if (modules.includes("INVOICE")) {
    return "/dashboard/invoice";
  }
  if (modules.includes("MEMBERS")) {
    return "/dashboard/members/all";
  }
  if (modules.includes("QUOTATION")) {
    return "/dashboard/quotation/";
  }
  if (modules.includes("PACKAGES")) {
    return "/dashboard/packages/all";
  }
  if (modules.includes("ROLE_MANAGEMENT")) {
    return "/dashboard/roles";
  }
  if (modules.includes("PAYMENTS_MANAGEMENT")) {
    return "/dashboard/payments";
  }
  if (modules.includes("LEADS")) {
    return "/dashboard/leads";
  }
  if (modules.includes("EQUIPMENT")) {
    return "/dashboard/equipment";
  }
  if (modules.includes("CAMERA_TO_CLOUD")) {
    return "/dashboard/camera-to-cloud";
  }
  if (modules.includes("MARKETPLACE")) {
    return "/dashboard/marketplace";
  }
  if (modules.includes("WHATSAPP_CRM")) {
    return "/dashboard/whatsapp";
  }

  return "/dashboard/albums/all";
};
